/**
 * RequestDocumentsSection - Read-only document viewer for requesting side
 * Shows uploaded documents for any bond with an executing agent (quote accepted),
 * including closed bonds. Supports inline viewing and downloading.
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  DocumentTextIcon,
  PaperClipIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { getFileCount, getDocumentPages, downloadDocumentPage } from '../../../../services/oathTransferBondService';
import { OathSectionCard, OathSpinner } from '../../../shared/OathUI';

const RequestDocumentsSection = ({ bondDetails }) => {
  const [fileCount, setFileCount] = useState(0);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState(null);
  const [viewerType, setViewerType] = useState('');
  const [viewerName, setViewerName] = useState('');
  const [viewerLoading, setViewerLoading] = useState(false);

  const transactionId = bondDetails?.transferBondTransactionID;
  const shouldShow = !!bondDetails?.executingAgentID;

  const fetchDocuments = useCallback(async () => {
    if (!transactionId || !shouldShow) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [countRes, pagesRes] = await Promise.all([
        getFileCount(transactionId),
        getDocumentPages(transactionId),
      ]);

      if (countRes.success) {
        setFileCount(countRes.count || 0);
      }
      if (pagesRes.success) {
        setPages(Array.isArray(pagesRes.data) ? pagesRes.data : []);
      }
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  }, [transactionId, shouldShow]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Clean up object URL when viewer closes
  useEffect(() => {
    return () => {
      if (viewerUrl) URL.revokeObjectURL(viewerUrl);
    };
  }, [viewerUrl]);

  const getPageId = (page) => page.id || page.pageId || page.documentPageId;
  const getPageName = (page, i) => page.name || page.fileName || page.description || `Document ${i + 1}`;
  const getExtension = (page) => (page.extension || page.name?.split('.').pop() || '').toLowerCase();

  const isViewable = (page) => {
    const ext = getExtension(page);
    return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
  };

  const handleView = async (page, i) => {
    const pageId = getPageId(page);
    if (!pageId) return;

    setViewerLoading(true);
    setViewerOpen(true);
    setViewerName(getPageName(page, i));

    const result = await downloadDocumentPage(transactionId, pageId);
    if (result.success) {
      const url = URL.createObjectURL(result.blob);
      setViewerUrl(url);
      setViewerType(result.contentType);
    } else {
      toast.error('Failed to load document');
      setViewerOpen(false);
    }
    setViewerLoading(false);
  };

  const handleDownload = async (page, i) => {
    const pageId = getPageId(page);
    if (!pageId) return;

    const result = await downloadDocumentPage(transactionId, pageId);
    if (result.success) {
      const url = URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.fileName || getPageName(page, i);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      toast.error('Failed to download document');
    }
  };

  const closeViewer = () => {
    setViewerOpen(false);
    if (viewerUrl) {
      URL.revokeObjectURL(viewerUrl);
      setViewerUrl(null);
    }
    setViewerType('');
    setViewerName('');
  };

  if (!shouldShow) return null;
  if (!loading && fileCount === 0 && pages.length === 0) return null;

  return (
    <>
      <OathSectionCard title="Uploaded Documents" icon={DocumentTextIcon} className="mb-6">
        {loading ? (
          <div className="flex justify-center py-6">
            <OathSpinner size="md" />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span>Total Files: <strong className="text-gray-900 dark:text-white">{fileCount}</strong></span>
            </div>

            {pages.length > 0 ? (
              <div className="space-y-2">
                {pages.map((page, i) => (
                  <div
                    key={getPageId(page) || i}
                    className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                  >
                    <PaperClipIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {getPageName(page, i)}
                      </p>
                      {(page.extension || page.pageNumber != null) && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {page.extension && <span className="uppercase">{page.extension}</span>}
                          {page.extension && page.pageNumber != null && <span> · </span>}
                          {page.pageNumber != null && <span>Page {page.pageNumber}</span>}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {isViewable(page) && (
                        <button
                          onClick={() => handleView(page, i)}
                          className="p-2 text-gray-500 hover:text-primary hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                          title="View"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDownload(page, i)}
                        className="p-2 text-gray-500 hover:text-primary hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                        title="Download"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : fileCount > 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {fileCount} document{fileCount !== 1 ? 's have' : ' has'} been uploaded for this transaction.
              </p>
            ) : null}
          </div>
        )}
      </OathSectionCard>

      {/* Inline Document Viewer Modal */}
      {viewerOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/60" onClick={closeViewer} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{viewerName}</h3>
                <button
                  onClick={closeViewer}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              {/* Content */}
              <div className="flex-1 min-h-0 overflow-auto p-1">
                {viewerLoading ? (
                  <div className="flex justify-center items-center py-20">
                    <OathSpinner size="lg" />
                  </div>
                ) : viewerType.includes('pdf') ? (
                  <iframe
                    src={viewerUrl}
                    className="w-full h-[75vh] rounded"
                    title={viewerName}
                  />
                ) : viewerType.startsWith('image/') ? (
                  <img
                    src={viewerUrl}
                    alt={viewerName}
                    className="max-w-full mx-auto rounded"
                  />
                ) : (
                  <div className="text-center py-12">
                    <p className="text-sm text-gray-500 dark:text-gray-400">This file type cannot be previewed.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RequestDocumentsSection;
